import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertOctagon, AlertTriangle, AlertCircle, Info, FileText, Layers,
  Wand2, CircleCheck, ChevronRight, Activity, Loader2,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AdminAPI } from '@/lib/admin-api-quality';
import { cn } from '@/lib/utils';

const SeverityTile = ({ label, value, tone, icon: Icon, sub }) => (
  <Card className={cn('p-3 border', tone)} data-testid={`admin-qa-severity-${label.toLowerCase()}`}>
    <div className="flex items-center justify-between gap-2">
      <div className="text-[10px] uppercase tracking-[0.16em] opacity-70">{label}</div>
      <Icon className="h-3.5 w-3.5" />
    </div>
    <div className="mt-1.5 font-display text-2xl font-semibold font-mono-num">{value}</div>
    {sub && <div className="mt-0.5 text-[10px] opacity-60">{sub}</div>}
  </Card>
);

export default function AdminRuleQA() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    AdminAPI.qa.dashboard().then(setData).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="admin-rule-qa-page">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-medium">Operations · Quality</div>
          <h1 className="mt-1.5 font-display text-xl sm:text-2xl font-semibold tracking-tight">Rule QA Dashboard</h1>
          <p className="mt-1 text-xs text-muted-foreground max-w-2xl">
            Operational health of the entire rewards engine — conflict counts, low-confidence rules, missing exclusions, stale rules, open drafts.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="gap-1.5" data-testid="admin-rule-qa-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Activity className="h-3.5 w-3.5" />} Refresh
        </Button>
      </div>

      {loading || !data ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-[92px] w-full rounded-xl" />)}
        </div>
      ) : (
        <>
          {/* Severity tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SeverityTile label="CRITICAL" value={data.severity_totals.critical} icon={AlertOctagon} tone="border-rose-500/40 bg-rose-500/5 text-rose-700 dark:text-rose-300" sub="must fix" />
            <SeverityTile label="HIGH"     value={data.severity_totals.high}     icon={AlertTriangle} tone="border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-300" sub="rule firing risks" />
            <SeverityTile label="MEDIUM"   value={data.severity_totals.medium}   icon={AlertCircle}   tone="border-primary/30 bg-primary/5 text-primary" sub="ops mistakes" />
            <SeverityTile label="ADVISORY" value={data.severity_totals.advisory} icon={Info}          tone="border-border bg-[hsl(var(--surface-2))] text-foreground/80" sub="quality signals" />
          </div>

          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SeverityTile label="Cards" value={data.totals.cards} icon={Layers} tone="border-border bg-[hsl(var(--surface-2))]" sub={`${data.totals.cards_with_active_version} with active version`} />
            <SeverityTile label="Open drafts" value={data.totals.open_drafts} icon={FileText} tone="border-border bg-[hsl(var(--surface-2))]" sub="awaiting review or publish" />
            <SeverityTile label="Parity runs" value={data.totals.parity_runs_total} icon={Activity} tone="border-border bg-[hsl(var(--surface-2))]" sub="all-time" />
            <SeverityTile label="Cards w/o active" value={data.totals.cards_without_active} icon={AlertOctagon} tone="border-border bg-[hsl(var(--surface-2))]" sub="needs initial publish" />
          </div>

          <Tabs defaultValue="conflicts">
            <TabsList>
              <TabsTrigger value="conflicts"  data-testid="admin-rule-qa-tab-conflicts">Cards with conflicts ({data.top_lists.cards_with_conflicts.length})</TabsTrigger>
              <TabsTrigger value="low_conf"   data-testid="admin-rule-qa-tab-lowconf">Low confidence rules ({data.top_lists.low_confidence_rules.length})</TabsTrigger>
              <TabsTrigger value="missing"    data-testid="admin-rule-qa-tab-missing">Missing exclusions ({data.top_lists.missing_exclusions.length})</TabsTrigger>
              <TabsTrigger value="stale"      data-testid="admin-rule-qa-tab-stale">Stale rules ({data.top_lists.stale_rules.length})</TabsTrigger>
              <TabsTrigger value="drafts"     data-testid="admin-rule-qa-tab-drafts">Open drafts ({data.top_lists.open_drafts.length})</TabsTrigger>
              <TabsTrigger value="no_mcc"     data-testid="admin-rule-qa-tab-nomcc">No MCC specificity ({data.top_lists.rules_without_mcc.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="conflicts" className="mt-3">
              <List items={data.top_lists.cards_with_conflicts} renderItem={(c) => (
                <div className="flex items-center gap-3" data-testid={`admin-qa-conflict-row-${c.card_id}`}>
                  <Layers className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    <div className="text-[10px] text-muted-foreground font-mono-num">{c.card_id}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {c.critical > 0 && <Badge variant="outline" className="text-[10px] bg-rose-500/10 text-rose-600 border-rose-500/30">{c.critical} crit</Badge>}
                    {c.high > 0 && <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-600 border-amber-500/30">{c.high} high</Badge>}
                    {c.medium > 0 && <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/30">{c.medium} med</Badge>}
                  </div>
                  <Link to={`/admin/cards/${c.card_id}/rules`} className="text-muted-foreground hover:text-foreground" data-testid={`admin-qa-conflict-open-${c.card_id}`}>
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
              )} emptyText="No cards with critical or high conflicts. Nice." />
            </TabsContent>

            <TabsContent value="low_conf" className="mt-3">
              <List items={data.top_lists.low_confidence_rules} renderItem={(r) => (
                <Row to={`/admin/cards/${r.card_id}/rules`} icon={Wand2} label={r.card_name} sub={r.message} idMono={r.rule_id} />
              )} emptyText="No low-confidence rules currently." />
            </TabsContent>

            <TabsContent value="missing" className="mt-3">
              <List items={data.top_lists.missing_exclusions} renderItem={(r) => (
                <Row to={`/admin/cards/${r.card_id}/rules`} icon={AlertTriangle} label={r.card_name} sub={r.message} idMono={r.code} />
              )} emptyText="All cards have the standard Indian exclusions declared." />
            </TabsContent>

            <TabsContent value="stale" className="mt-3">
              <List items={data.top_lists.stale_rules} renderItem={(r) => (
                <Row to={`/admin/cards/${r.card_id}/rules`} icon={AlertCircle} label={r.card_name} idMono={r.rule_id} />
              )} emptyText="No stale rules — every rule was verified recently." />
            </TabsContent>

            <TabsContent value="drafts" className="mt-3">
              <List items={data.top_lists.open_drafts} renderItem={(d) => (
                <Row to={`/admin/cards/${d.card_id}/rules`} icon={FileText} label={d.notes || 'Draft'} sub={`v${d.version} · by ${d.created_by || 'unknown'}`} idMono={d.id} />
              )} emptyText="No open drafts." />
            </TabsContent>

            <TabsContent value="no_mcc" className="mt-3">
              <List items={data.top_lists.rules_without_mcc} renderItem={(r) => (
                <Row to={`/admin/cards/${r.card_id}/rules`} icon={Info} label={r.card_name} idMono={r.rule_id} sub="Match by category only — add MCC for precise routing" />
              )} emptyText="Every rule has MCC specificity." />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}

const Row = ({ to, icon: Icon, label, sub, idMono }) => (
  <Link to={to} className="flex items-center gap-3 hover:bg-muted/30 rounded-md -mx-2 px-2 py-1.5 transition-colors group">
    <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="text-sm truncate">{label}</div>
      {sub && <div className="text-[10px] text-muted-foreground truncate">{sub}</div>}
    </div>
    {idMono && <code className="text-[10px] text-muted-foreground font-mono-num">{idMono}</code>}
    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 group-hover:text-foreground" />
  </Link>
);

const List = ({ items, renderItem, emptyText }) => (
  <Card className="p-2">
    {items.length === 0 ? (
      <div className="py-6 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
        <CircleCheck className="h-3.5 w-3.5 text-emerald-500" /> {emptyText}
      </div>
    ) : (
      <div className="space-y-0.5">
        {items.map((item, i) => <div key={item.id || item.card_id + '-' + i}>{renderItem(item)}</div>)}
      </div>
    )}
  </Card>
);
